/**
 * LA COULEUR DU BORD — de quoi prolonger l'image au lieu de la flouter.
 *
 * Quand la zone à préserver oblige l'image à reculer, il reste du vide sur les côtés.
 *
 * Premier essai : la même image, agrandie et floutée derrière (l'ambilight des
 * téléviseurs). Vu sur un vrai bandeau, ça ne tient pas — le flou montre le CENTRE de
 * l'image, pas la suite de son bord, et l'affiche orange de Sport Achat se retrouvait
 * bordée de taches pâles qui n'existent nulle part dessus.
 *
 * Deuxième essai : un dégradé vertical repris ligne à ligne du bord. Fidèle, et c'est
 * le problème : la sangle noire du sac touche le bord gauche, donc elle repartait en
 * traînée sombre à travers l'aplat orange.
 *
 * Ce qui reste : UNE COULEUR PAR CÔTÉ, la médiane des pixels du bord. Sur un fond uni
 * — le cas de presque toutes les affiches d'événement — on ne voit plus où l'image
 * s'arrête.
 *
 * Une médiane GLISSANTE, pour suivre un bord en dégradé, a été essayée entre les
 * deux : sur cette même affiche, elle laissait repasser un fantôme de la sangle. Une
 * couleur par côté, donc, et rien de plus malin.
 *
 * ⚠️ LA MÉDIANE, PAS LA MOYENNE. C'est elle qui ignore la sangle : un trait qui
 * traverse le bord sur un cinquième de sa hauteur ne déplace pas la médiane, alors
 * qu'il salit la moyenne. Prise canal par canal, elle peut composer une teinte qui
 * n'est sur aucun pixel ; sur un bord dominé par une couleur, c'est cette couleur.
 *
 * ⚠️ UNE LECTURE DE PIXELS DEMANDE LE CONSENTEMENT DU SERVEUR. Le seau public répond
 * `Access-Control-Allow-Origin: *` (vérifié), donc l'image chargée en
 * `crossOrigin="anonymous"` est lisible. Une image servie d'ailleurs, sans cet
 * en-tête, SOUILLE le canevas et `getImageData` lève : on rend alors `null`, et le
 * bandeau retombe sur sa couleur de marque. Jamais d'erreur à l'écran pour ça.
 */

/** Combien de pixels on prend le long d'un bord pour en tirer la médiane. */
const SAMPLES = 24;

/**
 * L'épaisseur de la tranche lue, en fraction de la largeur de l'image, et son
 * plafond en pixels.
 *
 * ⚠️ FINE. Essayée à 2 %, elle avalait le sac à dos qui commence juste après le bord
 * de l'affiche. Le bord, c'est le bord : quelques pixels, pas un échantillon de
 * l'image.
 */
const STRIP = 0.004;
const STRIP_MAX = 8;

export type Edges = {
  /** La couleur qui prolonge le bord gauche. */
  left: string;
  right: string;
  /** Entre les deux : ce qui comble un vide en haut ou en bas. */
  average: string;
};

/**
 * L'ADRESSE DE LA SONDE LISIBLE : la même image, sous un nom que le cache ne confond pas
 * avec celle du fond.
 *
 * ⚠️ CHROME RATAIT LA LECTURE, SAFARI NON (#548). Le fond du bandeau charge l'image
 * SANS CORS, et le seau ne renvoie alors ni `Access-Control-Allow-Origin` ni
 * `Vary: Origin` (il ne les pose que sur une requête qui porte un `Origin`, vérifié).
 * Chrome resservait cette réponse-là, depuis son cache, à la sonde `crossOrigin` :
 * refusée, donc pas de couleur de bord, et des côtés à la couleur de marque là où
 * Safari prolongeait le bord de l'image. Sans le fond, la même sonde lisait très bien.
 * Un paramètre en plus fait une autre entrée de cache, toujours chargée avec CORS.
 *
 * Seulement pour une adresse réseau : `data:` et `blob:` n'ont pas de cache à partager.
 * Une adresse SIGNÉE ne supporterait pas le paramètre ; elle tombe alors dans le repli
 * sans lecture, et le bandeau garde sa couleur de marque, comme toute image illisible.
 */
export function readableUrl(image: string): string {
  if (!/^(https?:)?\/\//i.test(image)) return image;
  const hash = image.indexOf("#");
  const base = hash < 0 ? image : image.slice(0, hash);
  const tail = hash < 0 ? "" : image.slice(hash);
  return `${base}${base.includes("?") ? "&" : "?"}gk-edges${tail}`;
}

const rgb = (c: number[]) => `rgb(${c[0]} ${c[1]} ${c[2]})`;

/**
 * LES ÉCHANTILLONS DES DEUX BORDS, lus dans les pixels recopiés par `readEdges` : une
 * ligne par échantillon, la tranche gauche puis la droite, chacune large de `strip`.
 * Chaque échantillon est la moyenne de sa tranche sur la ligne, canal par canal.
 */
export function stripSamples(
  pixels: ArrayLike<number>,
  strip: number,
  samples: number,
): { left: number[][]; right: number[][] } {
  const stride = strip * 2 * 4;
  const mean = (row: number, from: number) => {
    const sum = [0, 0, 0];
    for (let x = from; x < from + strip; x += 1) {
      const at = row * stride + x * 4;
      sum[0]! += pixels[at]!;
      sum[1]! += pixels[at + 1]!;
      sum[2]! += pixels[at + 2]!;
    }
    return sum.map((channel) => Math.round(channel / strip));
  };
  const left: number[][] = [];
  const right: number[][] = [];
  for (let row = 0; row < samples; row += 1) {
    left.push(mean(row, 0));
    right.push(mean(row, strip));
  }
  return { left, right };
}

/**
 * La distance entre deux couleurs.
 *
 * ⚠️ PAS AU CARRÉ (#548). La première version rendait le carré, « on ne compare que des
 * distances ». Mais c'est une SOMME qu'on compare : celle des carrés est la plus petite
 * pour l'échantillon le plus proche de la MOYENNE, et la médiane redevenait la moyenne
 * qu'elle devait remplacer. Sur l'affiche Sport Achat, dont le bord gauche est traversé
 * de traits bleus sur beaucoup de lignes, le côté sortait rgb(227 171 95) au lieu de
 * l'orange 252 189 96 du fond : une couture visible.
 */
function gap(a: number[], b: number[]): number {
  return Math.hypot(a[0]! - b[0]!, a[1]! - b[1]!, a[2]! - b[2]!);
}

/**
 * LA COULEUR LA PLUS CENTRALE d'un bord : celle dont la somme des distances aux autres
 * est la plus petite.
 *
 * Elle sort du lot d'échantillons, donc elle est SUR l'image. Sur un bord uni traversé
 * par un trait, c'est la couleur du fond ; sur un bord en deux blocs, celle du bloc le
 * plus large ; jamais un mélange des deux.
 */
export function median(rows: number[][]): number[] {
  if (rows.length === 0) return [0, 0, 0];
  let best = rows[0]!;
  let bestSum = Infinity;
  for (const row of rows) {
    let sum = 0;
    for (const other of rows) sum += gap(row, other);
    if (sum < bestSum) {
      bestSum = sum;
      best = row;
    }
  }
  return best;
}

export function readEdges(
  image: HTMLImageElement,
  samples = SAMPLES,
): Edges | null {
  const width = image.naturalWidth;
  const height = image.naturalHeight;
  if (!(width > 0) || !(height > 0)) return null;

  try {
    const strip = Math.min(STRIP_MAX, Math.max(1, Math.round(width * STRIP)));
    const canvas = document.createElement("canvas");
    canvas.width = strip * 2;
    canvas.height = samples;
    const paint = canvas.getContext("2d");
    if (!paint) return null;

    /* ⚠️ RECOPIÉES PIXEL POUR PIXEL, JAMAIS RÉDUITES PAR LE NAVIGATEUR (#548). La
       première version réduisait chaque tranche à une colonne de `samples` pixels et
       laissait le navigateur faire la moyenne. Chaque moteur réduit à sa façon :
       Chrome moyennait aussi la hauteur, donc les traits bleus qui touchent le bord de
       l'affiche Sport Achat salissaient chaque échantillon, et la médiane ne pouvait
       plus les écarter. Côté gauche rgb(220 167 95) sous Chrome, rgb(243 182 95)
       sous Safari, pour un orange 252 189 96 : une couture visible sous Chrome
       seulement. Une ligne par
       échantillon, copiée à l'échelle 1 sans lissage, donne les mêmes pixels partout ;
       la moyenne de la tranche se fait ici. */
    paint.imageSmoothingEnabled = false;
    for (let sample = 0; sample < samples; sample += 1) {
      const row = Math.min(height - 1, Math.floor(((sample + 0.5) * height) / samples));
      paint.drawImage(image, 0, row, strip, 1, 0, sample, strip, 1);
      paint.drawImage(image, width - strip, row, strip, 1, strip, sample, strip, 1);
    }

    const pixels = paint.getImageData(0, 0, strip * 2, samples).data;
    const { left, right } = stripSamples(pixels, strip, samples);
    const one = median(left);
    const two = median(right);
    return {
      left: rgb(one),
      right: rgb(two),
      average: rgb(median([...left, ...right])),
    };
  } catch {
    // Canevas souillé, image inaccessible : le bandeau garde sa couleur de marque.
    return null;
  }
}
